/**
 * Categorized References Section JavaScript
 * Handles parent category toggle, sub-category filtering, and search functionality
 */

class CategorizedReferences {
  constructor(section) {
    this.section = section;
    this.sectionId = section.dataset.sectionId;
    this.toggleLeftLabel = section.dataset.toggleLeftLabel;
    this.toggleRightLabel = section.dataset.toggleRightLabel;
    
    this.init();
  }

  init() {
    // Parent toggle elements
    this.parentToggle = this.section.querySelector('.parent-toggle-input');
    this.leftLabel = this.section.querySelector('[data-toggle-label="left"]');
    this.rightLabel = this.section.querySelector('[data-toggle-label="right"]');
    
    // Category elements
    this.categoryBoxes = this.section.querySelectorAll('.category-box');
    this.categoryContents = this.section.querySelectorAll('.collapsible-tabs__content');
    
    // Reference cards
    this.referenceCards = this.section.querySelectorAll('.reference-card');
    
    // Search elements
    this.searchInput = this.section.querySelector('.ref-search-input');
    this.searchClear = this.section.querySelector('.ref-search-clear');
    this.searchResultsInfo = this.section.querySelector('.ref-search-results-info');
    this.searchResultsCount = this.section.querySelector('.ref-search-results-count');
    
    // Wrapper for search active state
    this.wrapper = this.section.querySelector('.collapsible-tabs__wrapper');
    this.searchContainer = this.section.querySelector('.search-bar-container');
    
    if (!this.parentToggle) return;
    
    this.isSearchActive = false;
    this.originalActiveCategory = null;
    this.searchBarVisible = this.isSearchBarVisible();
    this.currentParentCategory = this.toggleLeftLabel; // Default to left
    
    this.bindEvents();
    this.updateParentCategoryVisibility();
    this.setInitialActiveCategory();
  }

  bindEvents() {
    // Parent toggle change event
    if (this.parentToggle) {
      this.parentToggle.addEventListener('change', () => {
        this.handleParentToggle();
      });
    }

    // Label click events
    if (this.leftLabel) {
      this.leftLabel.addEventListener('click', () => {
        if (this.parentToggle.checked) {
          this.parentToggle.checked = false;
          this.handleParentToggle();
        }
      });
    }

    if (this.rightLabel) {
      this.rightLabel.addEventListener('click', () => {
        if (!this.parentToggle.checked) {
          this.parentToggle.checked = true;
          this.handleParentToggle();
        }
      });
    }

    // Category click events
    this.categoryBoxes.forEach(box => {
      box.addEventListener('click', (e) => {
        e.preventDefault();
        if (!this.isSearchActive) {
          this.handleCategoryClick(box);
        } else {
          e.stopPropagation();
        }
      });
    });

    // Search input events
    if (this.searchInput && this.searchBarVisible) {
      this.searchInput.addEventListener('input', (e) => {
        this.handleSearch(e.target.value);
      });

      this.searchInput.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          this.clearSearch();
        }
      });
    }

    // Clear search button
    if (this.searchClear && this.searchBarVisible) {
      this.searchClear.addEventListener('click', () => {
        this.clearSearch();
      });
    }

    // Handle window resize
    window.addEventListener('resize', () => {
      const newVisibility = this.isSearchBarVisible();
      if (newVisibility !== this.searchBarVisible) {
        this.searchBarVisible = newVisibility;
        if (!this.searchBarVisible && this.isSearchActive) {
          this.clearSearch();
        }
      }
    });
  }

  handleParentToggle() {
    const isRightActive = this.parentToggle.checked;
    
    // Update current parent category
    this.currentParentCategory = isRightActive ? this.toggleRightLabel : this.toggleLeftLabel;
    
    // Update label styles
    if (this.leftLabel && this.rightLabel) {
      this.leftLabel.classList.toggle('inactive', isRightActive);
      this.rightLabel.classList.toggle('inactive', !isRightActive);
    }
    
    // Update visibility of sub-categories and reference cards
    this.updateParentCategoryVisibility();
    
    // Reset to show all or first visible category
    this.resetCategorySelection();
  }

  updateParentCategoryVisibility() {
    const activeParent = this.currentParentCategory;
    
    // Update category boxes visibility
    this.categoryBoxes.forEach(box => {
      const parentCategory = box.dataset.parentCategory;
      
      if (parentCategory === 'both' || parentCategory === activeParent) {
        box.classList.add('parent-visible');
      } else {
        box.classList.remove('parent-visible');
        box.classList.remove('active');
      }
    });

    // Update category content visibility
    this.categoryContents.forEach(content => {
      const parentCategory = content.dataset.parentCategory;
      
      // For "all" content, always allow it to be visible
      if (content.dataset.categoryContent === 'all') {
        return;
      }
      
      if (parentCategory === activeParent) {
        content.classList.add('parent-visible');
      } else {
        content.classList.remove('parent-visible');
        content.classList.remove('active');
      }
    });

    // Update reference cards in the "all" view
    this.referenceCards.forEach(card => {
      // Support multiple parent categories (comma-separated)
      const parentCategories = card.dataset.parentCategories || card.dataset.parentCategory || '';
      const parentCategoriesArray = parentCategories.split(',').map(p => p.trim());
      
      // Check if the active parent is in the card's parent categories
      const isVisible = parentCategoriesArray.some(p => p === activeParent);
      
      if (isVisible) {
        card.classList.add('parent-visible');
      } else {
        card.classList.remove('parent-visible');
      }
    });
  }

  resetCategorySelection() {
    // Find the first visible category box and activate it
    const showAllButton = this.section.querySelector('.category-box[data-category="all"]');
    
    if (showAllButton && showAllButton.classList.contains('parent-visible')) {
      this.handleCategoryClick(showAllButton);
    } else {
      const visibleCategories = this.section.querySelectorAll('.category-box.parent-visible');
      if (visibleCategories.length > 0) {
        this.handleCategoryClick(visibleCategories[0]);
      }
    }
  }

  handleCategoryClick(clickedBox) {
    const categoryId = clickedBox.dataset.category;
    
    // Remove active class from all boxes
    this.categoryBoxes.forEach(box => {
      box.classList.remove('active');
    });
    
    // Add active class to clicked box
    clickedBox.classList.add('active');
    
    // Hide all category contents
    this.categoryContents.forEach(content => {
      content.classList.remove('active');
    });
    
    // Show the selected category content
    const targetContent = this.section.querySelector(`[data-category-content="${categoryId}"]`);
    if (targetContent) {
      setTimeout(() => {
        targetContent.classList.add('active');
      }, 100);
    }
  }

  handleSearch(searchTerm) {
    const term = searchTerm.trim().toLowerCase();
    
    if (term === '') {
      this.clearSearch();
      return;
    }

    this.activateSearchMode();
    
    let matchCount = 0;
    const searchTerms = term.split(' ').filter(t => t.length > 0);
    
    this.referenceCards.forEach(card => {
      const keywords = card.dataset.keywords || '';
      const title = card.dataset.refTitle || '';
      // Support both old and new data attribute names
      const categories = card.dataset.refCategories || card.dataset.refCategory || '';
      const content = card.textContent.toLowerCase();
      
      const searchText = `${keywords} ${title} ${categories} ${content}`;
      
      // Check if all search terms match
      const matches = searchTerms.every(searchTerm => 
        searchText.includes(searchTerm)
      );
      
      if (matches) {
        card.classList.add('search-match');
        matchCount++;
        this.highlightSearchTerms(card, searchTerms);
      } else {
        card.classList.remove('search-match');
        this.removeHighlights(card);
      }
    });

    this.updateSearchResults(matchCount, term);
    this.showSearchResults();
  }

  activateSearchMode() {
    if (!this.isSearchActive) {
      this.isSearchActive = true;
      this.originalActiveCategory = this.section.querySelector('.category-box.active');
      
      if (this.wrapper) {
        this.wrapper.classList.add('search-active');
      }
      
      // Hide all category-specific contents
      this.categoryContents.forEach(content => {
        content.classList.remove('active');
      });
      
      // Show the "all" category content during search
      const allCategoryContent = this.section.querySelector('[data-category-content="all"]');
      if (allCategoryContent) {
        allCategoryContent.classList.add('active');
      } else if (this.categoryContents.length > 0) {
        this.categoryContents[0].classList.add('active');
      }
    }
  }

  clearSearch() {
    this.isSearchActive = false;
    
    if (this.searchInput) {
      this.searchInput.value = '';
    }
    
    if (this.wrapper) {
      this.wrapper.classList.remove('search-active');
    }
    
    // Hide search UI elements
    if (this.searchClear) {
      this.searchClear.style.display = 'none';
    }
    
    if (this.searchResultsInfo) {
      this.searchResultsInfo.style.display = 'none';
    }
    
    // Remove search classes and highlights
    this.referenceCards.forEach(card => {
      card.classList.remove('search-match');
      this.removeHighlights(card);
    });
    
    // Restore original category state
    this.restoreOriginalCategoryState();
  }

  restoreOriginalCategoryState() {
    // Hide all contents first
    this.categoryContents.forEach(content => {
      content.classList.remove('active');
    });
    
    // Restore the originally active category
    if (this.originalActiveCategory) {
      const categoryId = this.originalActiveCategory.dataset.category;
      const targetContent = this.section.querySelector(`[data-category-content="${categoryId}"]`);
      if (targetContent) {
        setTimeout(() => {
          targetContent.classList.add('active');
        }, 100);
      }
    }
  }

  showSearchResults() {
    if (this.searchClear) {
      this.searchClear.style.display = 'flex';
    }
    
    if (this.searchResultsInfo) {
      this.searchResultsInfo.style.display = 'block';
    }
  }

  updateSearchResults(count, term) {
    if (this.searchResultsCount) {
      if (count === 0) {
        this.searchResultsCount.textContent = `No references found for "${term}"`;
      } else if (count === 1) {
        this.searchResultsCount.textContent = `1 reference found for "${term}"`;
      } else {
        this.searchResultsCount.textContent = `${count} references found for "${term}"`;
      }
    }
  }

  highlightSearchTerms(card, searchTerms) {
    const title = card.querySelector('.reference-card__title');
    if (title && title.textContent) {
      let text = title.textContent;
      
      searchTerms.forEach(term => {
        if (term.length > 1) {
          const regex = new RegExp(`(${this.escapeRegex(term)})`, 'gi');
          text = text.replace(regex, '<span class="search-highlight">$1</span>');
        }
      });
      
      title.innerHTML = text;
    }
  }

  removeHighlights(card) {
    const highlights = card.querySelectorAll('.search-highlight');
    highlights.forEach(highlight => {
      const parent = highlight.parentNode;
      parent.replaceChild(document.createTextNode(highlight.textContent), highlight);
      parent.normalize();
    });
  }

  escapeRegex(string) {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  isSearchBarVisible() {
    if (!this.searchContainer) return false;
    
    const computedStyle = window.getComputedStyle(this.searchContainer);
    return computedStyle.display !== 'none';
  }

  setInitialActiveCategory() {
    // Set the first visible category as active if none is already active
    const activeBox = this.section.querySelector('.category-box.active.parent-visible');
    if (!activeBox) {
      const visibleCategories = this.section.querySelectorAll('.category-box.parent-visible');
      if (visibleCategories.length > 0) {
        this.handleCategoryClick(visibleCategories[0]);
      }
    }
  }
}

// Initialize all categorized references sections
function initCategorizedReferences() {
  const sections = document.querySelectorAll('.section--categorized-references');
  sections.forEach(section => {
    new CategorizedReferences(section);
  });
}

// Initialize when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  initCategorizedReferences();
});

// Initialize on theme editor section load
document.addEventListener('shopify:section:load', (event) => {
  const section = event.target.querySelector('.section--categorized-references');
  if (section) {
    new CategorizedReferences(section);
  }
});

// Re-initialize on section rerender in theme editor
document.addEventListener('shopify:section:reorder', () => {
  initCategorizedReferences();
});

